import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { ProgressSpinner } from "primereact/progressspinner";

import { Paths } from "@/core/constants/paths";
import { persistBffSession } from "@/modules/auth";
import type { BffAuthUser } from "@/modules/auth";
import styles from "./auth-callback-screen.module.scss";

function parseCallbackHash(hash: string): { token: string; user: BffAuthUser } | null {
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  const token = params.get("token");
  const userRaw = params.get("user");

  if (!token || !userRaw) return null;

  try {
    const user = JSON.parse(decodeURIComponent(userRaw)) as BffAuthUser;
    if (!user?.id || !user?.email) return null;
    return { token, user };
  } catch {
    return null;
  }
}

export function AuthCallbackScreen() {
  const location = useLocation();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");

  useEffect(() => {
    const parsed = parseCallbackHash(location.hash);
    if (!parsed) {
      setStatus("error");
      return;
    }

    persistBffSession({
      accessToken: "",
      idToken: "",
      internalToken: parsed.token,
      user: {
        id: parsed.user.id,
        email: parsed.user.email,
        name: parsed.user.name ?? "",
        roles: parsed.user.roles ?? [],
      },
    });
    setStatus("success");
  }, [location.hash]);

  if (status === "success") {
    return <Navigate to={Paths.HOME} replace />;
  }

  if (status === "error") {
    return <Navigate to={`${Paths.LOGIN}?error=auth_failed`} replace />;
  }

  return (
    <main className={styles.page} data-testid="auth-callback-screen" aria-busy="true">
      <ProgressSpinner strokeWidth="4" />
      <p className={styles.label}>Concluindo autenticação...</p>
    </main>
  );
}
